/**
 * Pont RDV réel → entraînement (réutilisabilité — Étape 7, cf. spec/reusability.md).
 *
 * Carte VIOLETTE distincte, en pied de débrief Coach Debrief : « ce point a lâché
 * sur ton vrai RDV → entraîne-toi dessus ». Volontairement d'une couleur à part
 * (violet) pour ne jamais être noyée sous les signaux simulateur. Le contenu
 * (question, bouton, couleur, secondaire) vient de la règle administrable.
 */
import { useTranslation } from "react-i18next";
import { Mic, Radio } from "lucide-react";
import type { RealMeetingBridgeCardData } from "../lib/api";
import { pickScoringLabel } from "../lib/scoring-i18n";

const COLOR_MAP: Record<string, string> = {
  navy: "#384872",
  violet: "#5A4BBE",
  green: "#1E7A3C",
  coral: "#C0423F",
  gold: "#926B00",
};
function resolveColor(c: string): string {
  return c.startsWith("#") ? c : COLOR_MAP[c] || "#5A4BBE";
}

export function RealMeetingBridgeCard({
  card,
  lang,
  onPrimary,
  onSecondary,
}: {
  card: RealMeetingBridgeCardData;
  lang: string;
  onPrimary: () => void;
  onSecondary?: () => void;
}) {
  const { t } = useTranslation();
  const en = lang === "en";
  const color = resolveColor(card.primary.color);
  const question = en ? card.primary.questionEn : card.primary.questionFr;
  const button = en ? card.primary.buttonEn : card.primary.buttonFr;
  const weakLabel = card.weakest ? pickScoringLabel(card.weakest, lang) : "";
  const secText = card.secondary ? (en ? card.secondary.textEn : card.secondary.textFr) : "";

  return (
    <div className="rounded-xl border-2 bg-white p-4 md:p-5" style={{ borderColor: color }}>
      <div className="flex items-center gap-3 md:gap-4 mb-3">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: color }}>
          <Radio className="h-6 w-6 md:h-7 md:w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">
            {t("realBridge.eyebrow")}
          </div>
          <h3 className="text-base md:text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
            {question}
          </h3>
          {card.weakest && (
            <p className="text-sm text-gray-600 mt-0.5">
              {t("realBridge.weakPoint", { label: weakLabel, score: card.weakest.scoreSurDix })}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onPrimary}
        className="w-full md:w-auto inline-flex items-center justify-center gap-2 text-base font-semibold px-5 h-12 rounded-lg shadow-md hover:shadow-lg transition-shadow text-white"
        style={{ backgroundColor: color }}
      >
        <Mic className="h-5 w-5" />
        {button}
      </button>
      {card.secondary && secText && onSecondary && (
        <div className="mt-3">
          <button onClick={onSecondary} className="text-sm font-medium" style={{ color: "#384872" }}>
            {secText} →
          </button>
        </div>
      )}
    </div>
  );
}
